import { useState } from "react";
import { Link } from "react-router";
import { useAuth } from "@/app/auth-context";
import { PageContainer } from "@/app/layouts/PageContainer";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, Users, Building, Settings, FileText, List, Box, Layers, Activity, 
  ChevronDown, ChevronRight, LayoutList
} from "lucide-react";

interface CardItem { to: string; label: string; perm?: string }
const groups: { title: string; icon: React.ElementType; items: CardItem[] }[] = [
  { title: "Clients & Accounts", icon: Building2, items: [
    { to: "/clients",  label: "Clients",  perm: "clients:read" },
    { to: "/accounts", label: "Accounts", perm: "accounts:read" },
    { to: "/invoices", label: "Invoices", perm: "invoices:read" },
    { to: "/payments", label: "Payments", perm: "payments:read" },
    { to: "/finance/reconciled-today", label: "Reconciled Today", perm: "reconciliation:read" },
    { to: "/finance/transactions",     label: "Transactions",     perm: "transactions:read" },
    // Plan III Task 21 — route added in Tasks 22/23/24
    { to: "/statements/runs", label: "Statement runs", perm: "statements:read" },
  ]},
  { title: "People", icon: Users, items: [
    { to: "/admin/employees", label: "Employees", perm: "employees:read" },
    { to: "/me",              label: "My profile" },
  ]},
  { title: "Org", icon: Building, items: [
    { to: "/admin/branches",     label: "Branches",     perm: "branches:read" },
    { to: "/admin/branch-types", label: "Branch types", perm: "branches:read" },
    { to: "/admin/companies",    label: "Companies",    perm: "branches:read" },
  ]},
  { title: "System", icon: Settings, items: [
    { to: "/admin/system-parameters", label: "System parameters", perm: "system-params:read" },
    { to: "/admin/next-numbers",      label: "Next numbers",      perm: "next-number:read" },
    { to: "/admin/file-mappings",     label: "File mappings",     perm: "lookups:read" },
    { to: "/admin/roles",             label: "Roles & permissions", perm: "system-admin:write" },
    { to: "/admin/users",             label: "Logins & access",     perm: "system-admin:write" },
  ]},
  { title: "Quotes", icon: FileText, items: [
    { to: "/admin/quote-templates", label: "Quote templates",  perm: "quote-templates:read" },
    { to: "/admin/quote-tandcs",    label: "Quote T&Cs",       perm: "quote-tandcs:read" },
    { to: "/admin/approval-types",  label: "Approval types",   perm: "approval-types:read" },
    { to: "/admin/job-status-codes", label: "Job status codes", perm: "job-status-codes:read" },
    { to: "/admin/invoice-statuses", label: "Invoice statuses", perm: "invoice-lookups:read" },
    { to: "/admin/account-terms",   label: "Account terms",    perm: "accounts:read" },
    { to: "/admin/quote-inclusions", label: "Quote inclusions", perm: "quote-inclusions:read" },
  ]},
  { title: "Lookups", icon: List, items: [
    { to: "/admin/printers",                label: "Printers",                       perm: "printers:read" },
    { to: "/admin/status-groups",           label: "Status groups",                  perm: "lookups:read" },
    { to: "/admin/issued-link-types",       label: "Issued link types",              perm: "lookups:read" },
    { to: "/admin/notification-types",      label: "Notification types",             perm: "lookups:read" },
    { to: "/admin/user-notification-types", label: "User notification types",        perm: "lookups:read" },
    { to: "/admin/delivery-methods",        label: "Delivery methods",               perm: "lookups:read" },
    { to: "/admin/stmt-delivery-methods",   label: "Statement delivery",             perm: "lookups:read" },
    { to: "/admin/transaction-types",       label: "Transaction types",              perm: "lookups:read" },
    { to: "/admin/email-templates",          label: "Email templates",                perm: "email-templates:read" },
    { to: "/admin/email-templates/inline-images", label: "Inline images",              perm: "email-templates:read" },
    { to: "/admin/from-email-types",        label: "From-email types",               perm: "lookups:read" },
    { to: "/admin/payment-methods",          label: "Payment methods",                perm: "payment-lookups:read" },
    { to: "/admin/payment-types",            label: "Payment types",                  perm: "payment-lookups:read" },
    { to: "/admin/payment-gateways",         label: "Payment gateways",               perm: "payment-lookups:read" },
  ]},
  { title: "Datasheets", icon: FileText, items: [
    { to: "/admin/datasheet-types", label: "Datasheet Types", perm: "datasheet-types:read" },
    { to: "/admin/datasheet-sizes", label: "Datasheet Sizes", perm: "datasheet:read" },
    { to: "/admin/artwork-templates", label: "Artwork templates", perm: "artwork-templates:read" },
  ]},
  { title: "Catalog", icon: Box, items: [
    { to: "/admin/catalog/products",   label: "Products",           perm: "products:read" },
    { to: "/admin/catalog/categories", label: "Product categories", perm: "products:read" },
  ]},
  { title: "BOM", icon: Layers, items: [
    { to: "/admin/bom/boms",          label: "All BOMs",      perm: "bom:read" },
    { to: "/admin/bom/templates",     label: "BOM templates", perm: "bom-templates:read" },
    { to: "/admin/bom/master-items",  label: "Master items",  perm: "bom-items:read" },
    { to: "/admin/catalog/suppliers", label: "Suppliers",     perm: "suppliers:read" },
    { to: "/admin/bom/price-tables",          label: "Price tables",    perm: "price-tables:read" },
    { to: "/admin/bom/price-table-templates", label: "Price templates", perm: "price-tables:read" },
    { to: "/admin/mfg-catalog",               label: "Mfg Catalog",     perm: "mfg-catalog:read" },
  ]},
  { title: "BOM lookups", icon: LayoutList, items: [
    { to: "/admin/bom/lookups/calc-types",    label: "Calc types",    perm: "bom-lookups:read" },
    { to: "/admin/bom/lookups/item-types",    label: "Item types",    perm: "bom-lookups:read" },
    { to: "/admin/bom/lookups/deco-methods",  label: "Deco methods",  perm: "bom-lookups:read" },
    { to: "/admin/bom/lookups/product-types", label: "Product types", perm: "bom-lookups:read" },
    { to: "/admin/bom/lookups/product-fittings", label: "Product fittings", perm: "bom-lookups:read" },
  ]},
  { title: "Operations", icon: Activity, items: [
    { to: "/admin/jobs-launcher", label: "Hangfire dashboard" },
    { to: "/admin/batch-queue",  label: "Batch Job Queue",  perm: "batchqueue:read" },
    { to: "/admin/user-audit",   label: "User audit log",   perm: "user-audit:read" },
    { to: "/admin/system-links", label: "System links",     perm: "lookups:read" },
    { to: "/admin/email-log",    label: "Email log",        perm: "system-admin:read" },
    { to: "/admin/spam-addresses", label: "Spam addresses", perm: "system-admin:read" },
    { to: "/admin/from-email-addresses", label: "From addresses", perm: "system-admin:read" },
  ]},
];

function CollapsibleSection({ title, icon: Icon, children }: { title: string, icon: React.ElementType, children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <section aria-labelledby={`grp-${title}`} className="space-y-4">
      <button 
        onClick={() => setOpen(!open)} 
        className="flex items-center gap-2 w-full text-left focus:outline-none group"
      >
        {open ? <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" /> : <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />}
        <Icon className="h-5 w-5 text-slate-700 group-hover:text-primary transition-colors" />
        <h2 id={`grp-${title}`} className="text-lg font-semibold tracking-tight text-slate-800 group-hover:text-primary transition-colors">{title}</h2>
      </button>
      {open && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 pl-7">
          {children}
        </div>
      )}
    </section>
  );
}

export function AdminLandingPage() {
  const { me, permissions } = useAuth();
  const isAdmin = me?.roles.includes("Admin") ?? false;
  const mfaPending = isAdmin && me?.mfaEnabled === false;
  return (
    <PageContainer>
      <PageHeader title="Administration" />
      <div className="space-y-8 mt-4">
        {mfaPending && (
          <Badge variant="destructive">MFA enrolment required — visit My Profile</Badge>
        )}
        {groups.map((g) => {
          const visible = g.items.filter((i) => !i.perm || permissions.has(i.perm));
          if (visible.length === 0) return null;
          return (
            <CollapsibleSection key={g.title} title={g.title} icon={g.icon}>
              {visible.map((i) => {
                const Icon = g.icon;
                return (
                  <Link 
                    key={i.to} 
                    to={i.to} 
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border bg-white shadow-sm hover:shadow-md hover:border-primary/40 transition-all group"
                  >
                    <div className="p-2 rounded-lg bg-slate-50 text-slate-500 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className="text-sm font-medium text-slate-700 group-hover:text-primary">{i.label}</span>
                  </Link>
                );
              })}
            </CollapsibleSection>
          );
        })}
      </div>
    </PageContainer>
  );
}
